import React from 'react';
import { Download } from 'lucide-react';
import SectionHeading from '../components/ui/SectionHeading';
import PricingCard from '../components/ui/PricingCard';

const Pricing: React.FC = () => {
  const plans = [
    {
      title: 'Premium Portfolio',
      price: '4.75%',
      description: 'Ideal for owners with 5+ rental units seeking comprehensive management.',
      features: [
        { name: 'Full-service management', included: true },
        { name: 'Priority response', included: true },
        { name: 'Monthly bookkeeping', included: true },
        { name: 'Quarterly performance report', included: true },
        { name: 'Quarterly market brief', included: true },
        { name: 'Proactive suggestions on how to optimize properties', included: true },
        { name: 'Tenant retention program', included: true },
        { name: 'Preferred vendor pricing', included: true },
        { name: 'Listing on MLS & rental sites (Apartments.com, Zillow, etc.)', included: true },
      ],
    },
    {
      title: 'SFR Signature',
      price: '7.75%',
      description: 'Perfect for owners with 1-4 rental properties who want hassle-free management.',
      features: [
        { name: 'Full-service management', included: true },
        { name: '24/7 maintenance coordination', included: true },
        { name: 'Monthly bookkeeping', included: true },
        { name: 'Online owner portal', included: true },
        { name: 'Tenant screening', included: true },
        { name: 'Lease renewals', included: true },
        { name: 'Professional photography', included: true },
        { name: 'Preferred vendor pricing', included: true },
        { name: 'Listing on MLS & rental sites (Apartments.com, Zillow, etc.)', included: true },
      ],
      popular: true,
    },
    {
      title: 'STR Select',
      price: '16.5%',
      description: 'Specialized vacation rental management with dynamic pricing and guest services.',
      features: [
        { name: 'Specialized vacation rental management', included: true },
        { name: 'Dynamic pricing optimization*', included: true },
        { name: 'Guest communication handling', included: true },
        { name: 'Professional cleaning', included: true },
        { name: 'Listing optimization across various platforms, including AirBnB and VRBO', included: true },
        { name: 'Amenity management', included: true },
        { name: 'Revenue maximization strategies', included: true },
      ],
    },
  ];

  return (
    <section id="pricing" className="py-20 bg-gradient-to-b from-[#f0fdbc]/30 via-[#f0fdbc]/30 to-[#f0fdbc]/20 relative">
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#FFF8ED] to-transparent"></div>
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#FFF8ED] to-transparent"></div>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <SectionHeading
          title="Pricing Plans"
          subtitle="Choose the management plan that best fits your property portfolio and level of service desired. The details and scope of the services and specific pricing will be discussed prior to any charges."
          className="text-2xl md:text-3xl lg:text-4xl"
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
          {plans.map((plan, index) => (
            <PricingCard
              key={index}
              title={plan.title}
              price={plan.price}
              description={plan.description}
              features={plan.features}
              popular={plan.popular}
            />
          ))}
        </div>

        <p className="text-dark-gray text-xs mt-8 text-center">
          *Dynamic pricing is often coordinated through a specialized third-party partner; cost varies by provider and is passed through to you at cost, not marked up.
        </p>

        <div className="text-center mt-8">
          <p className="text-dark-gray mb-4 text-lg">
            Not sure which plan is right for you? <a href="#contact" className="font-bold text-dark bg-[#ecff9a] px-2 py-1 rounded hover:bg-[#ecff9a]/80 transition-colors duration-200">Contact us</a> for a personalized consultation.
          </p>
          <a
            href="/pricing-details.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-secondary font-medium hover:underline"
          >
            <Download className="h-4 w-4" />
            Download the full side-by-side comparison chart (PDF)
          </a>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
